import type { Catalogs, Translation } from "@amtana/i18n/catalog";

import { FUNNEL_CATALOGS } from "./ops-funnel-copy.ts";
import type { FunnelCopyCatalog } from "./ops-funnel-copy.ts";
import { PAYMENT_CATALOGS } from "./ops-payment-copy.ts";
import type { PaymentCatalog } from "./ops-payment-copy.ts";
import { PLATFORM_CATALOGS } from "./ops-platform-copy.ts";
import type { PlatformCatalog } from "./ops-platform-copy.ts";
import { PROVIDER_CATALOGS } from "./ops-provider-copy.ts";
import type { ProviderCatalog } from "./ops-provider-copy.ts";
import { SEARCH_CATALOGS } from "./ops-search-copy.ts";
import type { SearchCopyCatalog } from "./ops-search-copy.ts";
import { SLACK_CATALOGS } from "./ops-slack-copy.ts";
import type { SlackCopyCatalog } from "./ops-slack-copy.ts";
import { TABLE_CATALOGS } from "./ops-table-copy.ts";
import type { TableCatalog } from "./ops-table-copy.ts";
import { TENANT_CATALOGS } from "./ops-tenant-copy.ts";
import type { TenantCatalog } from "./ops-tenant-copy.ts";

// The back office's own words (epic 09, epic 12), in English and Spanish (D240, which
// superseded D212's English-only back office). They sit in five files so none passes the size
// ceiling: this one holds the shared words, the overview, the document titles and the error
// copy, and puts the other four in one catalogue. A key lives in exactly one file.

export type OpsCopyKey = keyof OpsCatalog;
export type OpsCatalog = typeof EN &
  TenantCatalog &
  PlatformCatalog &
  ProviderCatalog &
  PaymentCatalog &
  SlackCopyCatalog &
  TableCatalog &
  FunnelCopyCatalog &
  SearchCopyCatalog;
type CoreCatalog = typeof EN;

const EN = {
  backOffice: "Back office",
  cancel: "Cancel",
  close: "Close",
  showMore: "Show more",
  never: "Never",
  none: "None",
  on: "On",
  off: "Off",
  yes: "Yes",
  no: "No",
  healthHealthy: "Healthy",
  healthSlow: "Slow",
  healthFailing: "Failing",
  healthStopped: "Stopped",

  // The reason every staff write names (ADR-005 point 5).
  fieldReason: "Reason",
  reasonHint: "Optional. When given, the audit keeps it.",
  visitReasonHint: "Required, a word or more. The person's organization sees it in its audit.",
  reasonPlaceholder: "Customer asked support to fix their plan",

  // Overview (S12.03).
  overviewTitle: "Overview",
  overviewDescription: "Who is on the platform, and whether it is running well.",
  overviewTenants: "Who is on it",
  overviewHealth: "How it runs",
  tileOrganizations: "Organizations",
  tilePeople: "Users",
  tilePaying: "Paying organizations",
  tilePayingHint: { one: "Out of 1 organization", other: "Out of {count} organizations" },
  tileCreditsSpent: "Credits spent this week",
  tileCreditsSpentHint: "Analysis credits, last 7 days",
  tileQueueLag: "Queue lag",
  tileNoLanes: "No lanes are installed",
  tileQueuesDraining: "Draining",
  tileQueueBehind: { one: "1 job waiting", other: "{count} jobs waiting" },
  tileOverdueWakes: { one: "1 overdue wake", other: "{count} overdue wakes" },
  tileParkedStreams: { one: "1 stream parked", other: "{count} streams parked" },
  tileDeadLetters: "Dead letters",
  tileNoDeadLetters: "None waiting",
  tileDeadLettersWaiting: {
    one: "1 job gave up and waits for a manager",
    other: "{count} jobs gave up and wait for a manager",
  },
  tileWorker: "Worker's last heartbeat",
  tileWorkerNeverReported: "No worker has reported yet",
  tileWorkerSilent: { one: "No heartbeat for 1 min", other: "No heartbeat for {count} min" },
  tileWorkerQuiet: "No worker is live right now",
  tileWorkersRunning: { one: "1 worker running", other: "{count} workers running" },
  tileStaleWorkers: { one: "1 stale worker", other: "{count} stale workers" },

  // Document titles, declared before any screen renders (ops.web.ts).
  titleOverview: "Back office · {product}",
  titleOrganizations: "Organizations · Back office · {product}",
  titleOrganization: "Organization · Back office · {product}",
  titlePeople: "Users · Back office · {product}",
  titlePerson: "Person · Back office · {product}",
  titleStaff: "Staff · Back office · {product}",
  titleFlags: "Flags · Back office · {product}",
  titleProviders: "Providers · Back office · {product}",
  titleQueues: "Queues · Back office · {product}",
  titleCompute: "Compute · Back office · {product}",

  // Error copy for codes the back office's contract raises (ops.web.ts).
  lastManagerTitle: "The platform needs a manager",
  lastManagerDescription: "Grant manage to someone else before revoking or lowering the last one.",
  deadLetterGoneTitle: "That dead letter is gone",
  deadLetterGoneDescription: "Someone may have already redriven or discarded it.",

  errorInvalidInput: "Check the fields and try again.",
} as const;

const ES: Translation<CoreCatalog> = {
  backOffice: "Back office",
  cancel: "Cancelar",
  close: "Cerrar",
  showMore: "Mostrar más",
  never: "Nunca",
  none: "Ninguno",
  on: "Activado",
  off: "Desactivado",
  yes: "Sí",
  no: "No",
  healthHealthy: "Sana",
  healthSlow: "Lenta",
  healthFailing: "Fallando",
  healthStopped: "Detenido",

  // The reason every staff write names (ADR-005 point 5).
  fieldReason: "Motivo",
  reasonHint: "Opcional. Si lo das, la auditoría lo guarda.",
  visitReasonHint: "Obligatorio, una palabra o más. La organización de la persona lo ve en su auditoría.",
  reasonPlaceholder: "El cliente pidió a soporte que arreglara su plan",

  // Overview (S12.03).
  overviewTitle: "Resumen",
  overviewDescription: "Quién está en la plataforma y si funciona bien.",
  overviewTenants: "Quién está",
  overviewHealth: "Cómo funciona",
  tileOrganizations: "Organizaciones",
  tilePeople: "Usuarios",
  tilePaying: "Organizaciones que pagan",
  tilePayingHint: { one: "De 1 organización", other: "De {count} organizaciones" },
  tileCreditsSpent: "Créditos gastados esta semana",
  tileCreditsSpentHint: "Créditos de análisis, últimos 7 días",
  tileQueueLag: "Atraso de las colas",
  tileNoLanes: "No hay carriles instalados",
  tileQueuesDraining: "Vaciándose",
  tileQueueBehind: { one: "1 trabajo en espera", other: "{count} trabajos en espera" },
  tileOverdueWakes: { one: "1 activación atrasada", other: "{count} activaciones atrasadas" },
  tileParkedStreams: { one: "1 stream detenido", other: "{count} streams detenidos" },
  tileDeadLetters: "Dead letters",
  tileNoDeadLetters: "Ninguno en espera",
  tileDeadLettersWaiting: {
    one: "1 trabajo se rindió y espera a un gestor",
    other: "{count} trabajos se rindieron y esperan a un gestor",
  },
  tileWorker: "Último latido del worker",
  tileWorkerNeverReported: "Ningún worker ha reportado aún",
  tileWorkerSilent: { one: "Sin latido hace 1 min", other: "Sin latido hace {count} min" },
  tileWorkerQuiet: "Ningún worker está activo ahora",
  tileWorkersRunning: { one: "1 worker corriendo", other: "{count} workers corriendo" },
  tileStaleWorkers: { one: "1 worker inactivo", other: "{count} workers inactivos" },

  // Document titles, declared before any screen renders (ops.web.ts).
  titleOverview: "Back office · {product}",
  titleOrganizations: "Organizaciones · Back office · {product}",
  titleOrganization: "Organización · Back office · {product}",
  titlePeople: "Usuarios · Back office · {product}",
  titlePerson: "Persona · Back office · {product}",
  titleStaff: "Staff · Back office · {product}",
  titleFlags: "Flags · Back office · {product}",
  titleProviders: "Proveedores · Back office · {product}",
  titleQueues: "Colas · Back office · {product}",
  titleCompute: "Cómputo · Back office · {product}",

  // Error copy for codes the back office's contract raises (ops.web.ts).
  lastManagerTitle: "La plataforma necesita un gestor",
  lastManagerDescription: "Da gestión a otra persona antes de revocar o bajar al último.",
  deadLetterGoneTitle: "Ese dead letter ya no está",
  deadLetterGoneDescription: "Puede que alguien ya lo haya reintentado o descartado.",

  errorInvalidInput: "Revisa los campos e inténtalo de nuevo.",
};

export const OPS_CATALOGS: Catalogs<OpsCatalog> = {
  en: {
    ...EN,
    ...TENANT_CATALOGS.en,
    ...PLATFORM_CATALOGS.en,
    ...PROVIDER_CATALOGS.en,
    ...PAYMENT_CATALOGS.en,
    ...SLACK_CATALOGS.en,
    ...TABLE_CATALOGS.en,
    ...FUNNEL_CATALOGS.en,
    ...SEARCH_CATALOGS.en,
  },
  es: {
    ...ES,
    ...TENANT_CATALOGS.es,
    ...PLATFORM_CATALOGS.es,
    ...PROVIDER_CATALOGS.es,
    ...PAYMENT_CATALOGS.es,
    ...SLACK_CATALOGS.es,
    ...TABLE_CATALOGS.es,
    ...FUNNEL_CATALOGS.es,
    ...SEARCH_CATALOGS.es,
  },
};
